import * as Sentry from '@sentry/nextjs'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { InvalidTokenError, invalidTokenErrorMessages } from 'shared/errors/invalid-token-error'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'

const httpClient = axios.create()

httpClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      if (error.response.status === 401) {
        if (invalidTokenErrorMessages.includes(error.response.data.message)) {
          throw new InvalidTokenError()
        } else if (error.response.data?.redirect) {
          throw new UnauthorizedResponse(error.response.data?.redirect)
        } else {
          Sentry.captureMessage('Unauthorized response', {
            level: 'error',
            extra: {
              responseData: error.response.data,
            },
          })
        }
      } else {
        throw error
      }
    }
  },
)

let isJWTRegenerated = false

const regenerateJWT = (): Promise<AxiosResponse<unknown, unknown, {}>> =>
  httpClient.post('/api/user/token/regenerate')

export const baseFetcher = <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  httpClient
    .get(url, config)
    .then(res => res.data)
    .catch(async error => {
      if (error instanceof UnauthorizedResponse) {
        globalThis.location.assign(error.redirect)
      } else if (error instanceof InvalidTokenError) {
        if (isJWTRegenerated) {
          Sentry.captureException(error, {
            extra: {
              context: 'Invalid token error',
            },
          })
        } else {
          try {
            // NOTE: Generating a new JWT
            await regenerateJWT()
            isJWTRegenerated = true
            // NOTE: repeat the same request
            return baseFetcher(url, config)
          } catch (error) {
            if (error instanceof UnauthorizedResponse) {
              globalThis.location.assign(error.redirect)
            }
          }
        }
      }
      throw error
    })

export default httpClient
